import { ONBOARDING_STEPS, PRODUCT_NAME, consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Badge, Button, Logo, MAIN_CONTENT_ID, Skeleton, SkipLink, Stepper, ThemeToggle, toast } from "@sagot/ui";
import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { StepConfigure } from "./step-configure";
import { StepGoLive } from "./step-go-live";
import { StepKnowledge } from "./step-knowledge";
import { StepOrganization } from "./step-organization";
import { StepTest } from "./step-test";
import { useSetupCopy } from "./use-catalog";
import { readOnboarding, useOnboardingWorkspace, useSaveProgress, useStepParam } from "./use-onboarding";

const SAVE_EXIT_DELAY_MS = 700;

function Header({ name, stepIndex, onSaveExit }: { name: string | undefined; stepIndex: number; onSaveExit: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="mx-auto flex h-14 max-w-5xl items-center gap-3 px-4 sm:px-6">
      <AppLink href="/" className="shrink-0" aria-label={t("auth.aside.homeLabel", { product: PRODUCT_NAME })}>
        <Logo />
      </AppLink>
      <span className="hidden h-6 w-px bg-line sm:block" />
      {name ? (
        <span className="hidden text-sm font-semibold text-fg-muted sm:block">
          {t("onboarding.setupOf")} <span className="text-fg">{name}</span>
        </span>
      ) : null}
      <div className="ml-auto flex items-center gap-2">
        <Badge tone="neutral" className="tabular-nums">
          {t("onboarding.stepCount", { current: stepIndex + 1, total: ONBOARDING_STEPS.length })}
        </Badge>
        <ThemeToggle size="sm" labels={{ trigger: t("theme.toggle"), heading: t("theme.heading"), light: t("theme.light"), dark: t("theme.dark"), system: t("theme.system") }} />
        <Button variant="secondary" size="sm" onClick={onSaveExit}>
          {t("onboarding.saveExit")}
        </Button>
      </div>
    </div>
  );
}

/** `/onboarding` — 5-step workspace setup (mockups/auth/onboarding.html, screens §1.8). */
export function OnboardingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [, setParams] = useSearchParams();
  const setup = useOnboardingWorkspace();
  const [stepIndex, setStep] = useStepParam();
  const save = useSaveProgress(setup.workspace);
  const dashboard = setup.workspace ? consoleRoutes.dashboard(setup.workspace.slug) : "/";
  const copy = useSetupCopy(setup.workspace);
  const assistantName = readOnboarding(setup.workspace).data.agent_name ?? copy.assistantName;

  useEffect(() => {
    document.title = t("onboarding.title", { product: PRODUCT_NAME });
  }, [t]);

  const saveExit = async () => {
    try {
      await save(stepIndex + 1, {});
    } finally {
      toast.success(t("onboarding.saved"));
      setTimeout(() => void navigate(dashboard), SAVE_EXIT_DELAY_MS);
    }
  };
  const finish = async () => {
    toast.success(t("onboarding.live.finished"));
    await navigate(dashboard, { replace: true });
  };
  const next = () => { setStep(stepIndex + 1); };
  const back = () => { setStep(stepIndex - 1); };
  const onOrganization = (target: { slug: string; step: number }) => {
    setParams({ ws: target.slug, step: String(target.step) });
  };
  // Steps past the first need a workspace; without one the wizard starts at the organization step.
  const index = setup.workspace ? stepIndex : 0;
  const steps = ONBOARDING_STEPS.map((id) => ({ id, label: t(`onboarding.steps.${id}`) }));
  const common = { workspace: setup.workspace, onBack: back };

  return (
    <div className="min-h-screen bg-canvas">
      <SkipLink label={t("shell.skipToContent")} />
      <header className="sticky top-0 z-30 border-b border-line bg-surface">
        <Header name={setup.workspace?.name} stepIndex={index} onSaveExit={() => void saveExit()} />
        <div className="mx-auto max-w-5xl px-4 pb-3 sm:px-6">
          <Stepper steps={steps} current={index} onStepClick={setup.workspace ? setStep : undefined} aria-label={t("onboarding.stepsLabel")} doneLabel={t("onboarding.stepDone")} />
        </div>
      </header>
      <main id={MAIN_CONTENT_ID} tabIndex={-1} className="mx-auto max-w-5xl px-4 py-8 outline-none sm:px-6 lg:py-10">
        {setup.isPending ? (
          <div className="space-y-6" aria-busy="true">
            <Skeleton className="h-8 w-[320px] max-w-full" />
            <Skeleton className="h-4 w-[480px] max-w-full" />
            <Skeleton className="h-72" />
          </div>
        ) : index === 0 ? (
          <StepOrganization key={setup.workspace?.id ?? "new"} workspace={setup.workspace} onNext={onOrganization} dashboardHref={dashboard} />
        ) : index === 1 ? (
          <StepKnowledge {...common} onNext={next} />
        ) : index === 2 ? (
          <StepConfigure {...common} onNext={next} />
        ) : index === 3 ? (
          <StepTest workspaceSlug={setup.workspace?.slug} assistantName={assistantName} sampleQuestions={copy.starterQuestions} onBack={back} onNext={next} />
        ) : (
          <StepGoLive {...common} onFinish={finish} />
        )}
      </main>
    </div>
  );
}
